import { compareDatesOnly, convertToDate } from "../utils/dates";
import { Participant, ParticipantWithId } from "../interfaces/IParticipants";

/**
 * Hook do walidacji zawodników
 * Zgodny z zasadą pojedynczej odpowiedzialności - tylko walidacja
 */
export const useParticipantValidation = () => {
  /**
   * Waliduje czy zawodnik o podanych danych już istnieje
   * @param excludeParticipantId - Opcjonalny ID zawodnika do wykluczenia z sprawdzenia (używane przy edycji)
   */
  const validateDuplicate = (
    participants: ParticipantWithId[],
    newParticipant: Participant,
    excludeParticipantId?: string
  ): boolean => {
    const newBirthDate = convertToDate(newParticipant.birthDate);

    return participants.some((existing) => {
      // Pomijamy zawodnika o danym ID (przy edycji nie chcemy porównywać z samym sobą)
      if (excludeParticipantId && existing.id === excludeParticipantId) {
        return false;
      }

      const nameMatch =
        existing.firstName.toLowerCase() === newParticipant.firstName.toLowerCase() &&
        existing.lastName.toLowerCase() === newParticipant.lastName.toLowerCase();

      if (!nameMatch) {
        return false;
      }

      const existingBirthDate = convertToDate(existing.birthDate);
      return compareDatesOnly(existingBirthDate, newBirthDate);
    });
  };

  /**
   * Waliduje format imienia lub nazwiska
   * Sprawdza: długość (2-100 znaków), czy nie zaczyna się/kończy znakiem "-" i czy nie ma wielokrotnych "-" obok siebie
   */
  const validateNameFormat = (name: string, fieldName: string): string | null => {
    if (!name || !name.trim()) {
      return null; // Pusta wartość jest obsługiwana przez inną walidację
    }

    const trimmedName = name.trim();

    // Sprawdź długość (min 2, max 100 znaków)
    if (trimmedName.length < 2) {
      return `${fieldName} musi mieć co najmniej 2 znaki`;
    }
    if (trimmedName.length > 50) {
      return `${fieldName} nie może mieć więcej niż 50 znaków`;
    }

    // Sprawdź czy zaczyna się od "-"
    if (trimmedName.startsWith("-")) {
      return `${fieldName} nie może zaczynać się od znaku "-"`;
    }

    // Sprawdź czy kończy się na "-"
    if (trimmedName.endsWith("-")) {
      return `${fieldName} nie może kończyć się znakiem "-"`;
    }

    // Sprawdź czy ma ciąg dwóch lub więcej znaków "-" obok siebie
    if (trimmedName.includes("--")) {
      return `${fieldName} nie może zawierać wielokrotnych znaków "-" obok siebie`;
    }

    return null;
  };

  /**
   * Sprawdza czy dane zawodnika są poprawne
   */
  const validateParticipantData = (participant: Participant): string[] => {
    const errors: string[] = [];

    if (!participant.firstName?.trim()) {
      errors.push("Imię jest wymagane");
    } else {
      // Walidacja formatu imienia
      const firstNameError = validateNameFormat(participant.firstName, "Imię");
      if (firstNameError) {
        errors.push(firstNameError);
      }
    }

    if (!participant.lastName?.trim()) {
      errors.push("Nazwisko jest wymagane");
    } else {
      // Walidacja formatu nazwiska
      const lastNameError = validateNameFormat(participant.lastName, "Nazwisko");
      if (lastNameError) {
        errors.push(lastNameError);
      }
    }

    if (!participant.birthDate) {
      errors.push("Data urodzenia jest wymagana");
    }

    if (!participant.gender) {
      errors.push("Płeć jest wymagana");
    }

    return errors;
  };

  return {
    validateDuplicate,
    validateParticipantData,
  };
};
